import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export {}; // module safety

const BASE_URL = "/api";

type ContactPersonData = {
  merchant_id: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  department_role: string;
  is_completed: boolean;
  updated_at?: string;
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "start",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

function Input({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid #cbd5e1",
        background: disabled ? "#f1f5f9" : "#fff",
        color: "#0f172a",
        fontWeight: 700,
        outline: "none",
      }}
    />
  );
}

function ReadonlyValue({ value }: { value: string }) {
  return (
    <div
      style={{
        fontWeight: 500,
        color: "#334155",
        whiteSpace: "pre-wrap",
        lineHeight: 1.6,
        padding: "10px 0",
      }}
    >
      {value && String(value).trim() ? String(value) : <span style={{ color: "#94a3b8" }}>—</span>}
    </div>
  );
}

function ReadonlyInputBox({
  label,
  value,
  required,
}: {
  label: string;
  value: string | undefined | null;
  required?: boolean;
}) {
  const displayValue = value && String(value).trim() ? String(value) : "—";

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>

      <div
        style={{
          width: "100%",
          height: 44,
          padding: "0 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#f8fafc",
          color: displayValue === "—" ? "#94a3b8" : "#0f172a",
          fontSize: 14,
          fontWeight: 700,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
        }}
      >
        {displayValue}
      </div>
    </div>
  );
}


export default function StaffFormViewContactPerson() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ContactPersonData | null>(null);
  const [error, setError] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // editable fields
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [departmentRole, setDepartmentRole] = useState("");

  const load = async () => {
    if (!merchantId) return;

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form7_contact_person`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "fetch failed");

      const d = json?.data as ContactPersonData;
      setData(d || null);

      // set form state
      setContactName(d?.contact_name || "");
      setContactPhone(d?.contact_phone || "");
      setContactEmail(d?.contact_email || "");
      setDepartmentRole(d?.department_role || "");

      setLoading(false);
    } catch (e: any) {
      setError(e?.message || "フォーム⑦（ご担当者情報）の取得に失敗しました。");
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const onSave = async () => {
    if (!merchantId) return;

    try {
      setSaving(true);
      setError("");

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form7_contact_person`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact_name: contactName,
          contact_phone: contactPhone,
          contact_email: contactEmail,
          department_role: departmentRole,
        }),
      });

      const json = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error(json?.detail || "save failed");

      setIsEditing(false);
      await load();
      alert("保存しました。");
    } catch (e: any) {
      setError(e?.message || "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    if (!merchantId) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position -= pdfHeight;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`フォーム7_ご担当者情報_${merchantId}.pdf`);
    } catch (e) {
      console.error(e);
      alert("PDFの作成に失敗しました。もう一度お試しください。");
    } finally {
      setDownloading(false);
    }
  };

  const canEdit = !!data?.is_completed;
  const disableInputs = !isEditing || saving;

  return (
    <StaffLayout title="フォーム⑦ ご担当者情報（確認）" active="progress">
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate(`/staff/progress/${merchantId}`)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#0f172a",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          進捗詳細へ戻る
        </button>

        <button
          onClick={downloadPdf}
          disabled={downloading || loading || !!error}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: downloading ? "#1e293b" : "#0b1f3b",
            color: "#fff",
            fontWeight: 900,
            cursor: downloading ? "not-allowed" : "pointer",
            opacity: downloading || loading || !!error ? 0.8 : 1,
          }}
          title={loading ? "読み込み完了後にダウンロードできます" : ""}
        >
          {downloading ? "PDF作成中..." : "PDFダウンロード"}
        </button>

        <button
          onClick={() => setIsEditing((v) => !v)}
          disabled={!canEdit || loading || saving}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: !canEdit ? "#e2e8f0" : isEditing ? "#fff" : "#0b1f3b",
            color: !canEdit ? "#64748b" : isEditing ? "#0b1f3b" : "#fff",
            fontWeight: 900,
            cursor: !canEdit ? "not-allowed" : "pointer",
          }}
          title={!canEdit ? "加盟店が保存完了後に編集できます" : ""}
        >
          {isEditing ? "編集をやめる" : "編集する"}
        </button>

        {isEditing && (
          <button
            onClick={onSave}
            disabled={saving}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #16a34a",
              background: saving ? "#166534" : "#16a34a",
              color: "#fff",
              fontWeight: 900,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.85 : 1,
            }}
          >
            {saving ? "保存中..." : "保存"}
          </button>
        )}
      </div>

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div ref={contentRef} style={{ display: "grid", gap: 12, background: "#ffffff" }}>
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>フォーム状態</div>
            <div style={{ fontWeight: 900, color: data?.is_completed ? "#166534" : "#9a3412" }}>
              {data?.is_completed ? "完了（保存済み）" : "未完了（または未保存）"}
            </div>
            <div style={{ marginTop: 6, color: "#475569", fontWeight: 800, fontSize: 13 }}>
              加盟店ID：{merchantId}
              {data?.updated_at ? `　｜　最終更新：${data.updated_at}` : ""}
            </div>
            {!data?.is_completed && (
              <div style={{ marginTop: 8, color: "#9a3412", fontWeight: 800, fontSize: 13 }}>
                ※ 加盟店が「保存完了」になるまで、編集はできません。
              </div>
            )}
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              background: "#fff",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 12,
                fontWeight: 900,
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              入力内容（{isEditing ? "編集モード" : "確認モード"}）
            </div>

            {!isEditing ? (
  <div style={{ padding: 12 }}>
    <div
      style={{
        border: "1px solid #cbd5e1",
        borderRadius: 12,
        overflow: "hidden",
        background: "#fff",
      }}
    >
      <div
        style={{
          padding: "10px 12px",
          fontWeight: 900,
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        ご担当者様情報（自動反映）
      </div>

      <div style={{ padding: 12 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            gap: 20,
            alignItems: "start",
          }}
        >
          <ReadonlyInputBox label="窓口担当者名（自動反映）" value={contactName} required />
          <ReadonlyInputBox label="部署・役職" value={departmentRole} />
          <ReadonlyInputBox label="担当者電話番号" value={contactPhone} required />
          <ReadonlyInputBox label="Email" value={contactEmail} required />
        </div>
      </div>
    </div>
  </div>
) : (
  <>
    <Row label="ご担当者名">
      <Input value={contactName} onChange={setContactName} disabled={disableInputs} />
    </Row>

    <Row label="電話番号">
      <Input value={contactPhone} onChange={setContactPhone} disabled={disableInputs} />
    </Row>

    <Row label="メールアドレス">
      <Input value={contactEmail} onChange={setContactEmail} disabled={disableInputs} />
    </Row>

    <Row label="部署・役職">
      <Input
        value={departmentRole}
        onChange={setDepartmentRole}
        disabled={disableInputs}
        placeholder="例）総務部 / 係長 など"
      />
    </Row>
  </>
)}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
